// src/components/ProtectedRoute.jsx
import React, { useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

/** Mini skeleton, remplace par ton composant UI si besoin */
function RouteSkeleton() {
  return (
    <div style={{ minHeight: "60vh", padding: 24 }}>
      <div style={{ width: 220, height: 20, background: "#eee", marginBottom: 12 }} />
      <div style={{ width: "100%", height: 120, background: "#eee" }} />
    </div>
  );
}

/**
 * ProtectedRoute (v2)
 *
 * Props:
 * - allowedRoles: ["expert","admin",...]
 * - requirePlan: ex "premium" / "art_advisor_premium" / "family_office" etc.
 * - requireActiveSubscription: true/false
 * - requireApproved: true/false  (redirige /pending-approval si profile.approved === false)
 * - requirePartnerType: ex "transport" | "insurance" | "valuation"
 * - redirectTo: override route si forbidden_role
 */
export default function ProtectedRoute({
  children,
  allowedRoles,
  requirePlan,
  requireActiveSubscription,
  requireApproved,
  requirePartnerType,
  redirectTo = "/",
}) {
  const { isAuthenticated, user, profile, loading, profileLoading, subscription } = useAuth();
  const location = useLocation();

  const gate = useMemo(() => {
    // 1) Auth/Profile loading
    if (loading || profileLoading) return { status: "loading_auth" };

    // 2) Pas connecté
    if (!isAuthenticated || !user?.id) {
      return { status: "redirect_login" };
    }

    // 3) Rôle (on prend profile.role en priorité)
    const role = profile?.role || user?.user_metadata?.role || user?.app_metadata?.role || null;

    if (allowedRoles?.length) {
      const roleStr = String(role || "").toLowerCase();
      const allowed = allowedRoles.map((r) => String(r || "").toLowerCase());
      if (!allowed.includes(roleStr)) {
        return { status: "forbidden_role", role: roleStr };
      }
    }

    // 4) Approved (si requis)
    if (requireApproved && profile?.approved === false) {
      return { status: "pending_approval" };
    }

    // 5) Partner type (si requis)
    if (requirePartnerType) {
      const got = String(profile?.partner_type || "").toLowerCase();
      const want = String(requirePartnerType || "").toLowerCase();
      if (!got || got !== want) {
        return { status: "forbidden_partner_type", got, want };
      }
    }

    // 6) Abonnement / plan
    const plan = profile?.plan || subscription?.plan || profile?.tier || null;

    // ⚠️ Dans ta table profiles: stripe_subscription_status (pas subscription_status)
    const stripeStatus = profile?.stripe_subscription_status || null;
    const subStatus = subscription?.status || null;

    const subActive =
      String(stripeStatus || "").toLowerCase() === "active" ||
      String(subStatus || "").toLowerCase() === "active" ||
      String(subStatus || "").toLowerCase() === "trialing" || // optionnel
      false;

    if (requireActiveSubscription && !subActive) {
      return { status: "needs_subscription" };
    }

    if (requirePlan && plan !== requirePlan) {
      return { status: "needs_upgrade", plan, requirePlan };
    }

    return { status: "ok" };
  }, [
    loading,
    profileLoading,
    isAuthenticated,
    user?.id,
    user?.user_metadata?.role,
    user?.app_metadata?.role,
    profile?.role,
    profile?.approved,
    profile?.partner_type,
    profile?.plan,
    profile?.tier,
    profile?.stripe_subscription_status,
    subscription?.plan,
    subscription?.status,
    allowedRoles,
    requirePlan,
    requireActiveSubscription,
    requireApproved,
    requirePartnerType,
  ]);

  // Rendu selon l’état
  if (gate.status === "loading_auth") return <RouteSkeleton />;

  if (gate.status === "redirect_login") {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (gate.status === "pending_approval") {
    return <Navigate to="/pending-approval" replace state={{ from: location }} />;
  }

  if (gate.status === "forbidden_partner_type") {
    const to = process.env.NODE_ENV === "production" ? "/unauthorized" : redirectTo;
    return (
      <Navigate
        to={to}
        replace
        state={{ from: location, requirePartnerType, got: gate.got, want: gate.want }}
      />
    );
  }

  if (gate.status === "forbidden_role") {
    return <Navigate to={redirectTo} replace state={{ from: location, role: gate.role }} />;
  }

  if (gate.status === "needs_subscription") {
    return <Navigate to="/billing" replace state={{ from: location }} />;
  }

  if (gate.status === "needs_upgrade") {
    return <Navigate to="/pricing" replace state={{ from: location, requirePlan }} />;
  }

  return children;
}